// Floating right-side menu per Figma 1:3482. Each button is a character
// illustration with an empty pill baked into the bottom of the image; we
// overlay the localized label text on top of that pill.

import { useMemo } from 'react';
import { useActiveSection } from '../hooks/useActiveSection';
import { trackStoreClick } from '../lib/analytics';
import { useLanguage } from '../i18n/useLanguage';
import { appStoreUrl, googlePlayUrl } from '../lib/storeLinks';

// Order matches <main> in App.tsx. Drives the mobile panel's
// per-section background + visibility behaviour (see below). Kept as a
// module-level constant + memoized below so useActiveSection doesn't
// re-subscribe on every render.
const SECTION_IDS = ['home', 'register', 'story', 'features', 'staff', 'campaign'] as const;

// Sections where the mobile panel paints the white-fade gradient.
const GRADIENT_SECTIONS = new Set<string>(['home', 'register']);
// Sections where the mobile panel sits on a fully transparent
// background so the section's own art reads through.
const TRANSPARENT_SECTIONS = new Set<string>(['story', 'features']);

interface CTAItem {
  key: 'register' | 'creator' | 'topup' | 'event';
  href: string;
  src: string;
  label: string;
}

// Per Figma 120:2369 the rail now ships four buttons (was three). The
// middle "campaign" slot is renamed to "ครีเอเตอร์" with a new
// character illustration, and a fourth "อีเวนต์" button is appended.
const ITEMS: CTAItem[] = [
  // {
  //   key: 'register',
  //   href: '#register',
  //   src: '/images/cta-register.png',
  //   label: 'ลงทะเบียน',
  // },
  {
    key: 'creator',
    href: '#campaign',
    src: '/images/cta-creator.png',
    label: 'ครีเอเตอร์',
  },
  {
    key: 'topup',
    href: '#',
    src: '/images/cta-topup.png',
    label: 'เติมเงิน',
  },
  {
    key: 'event',
    href: '#',
    src: '/images/cta-event.png',
    label: 'อีเวนต์',
  },
];

export default function FloatingCTARail() {
  const { lang } = useLanguage();
  // Per-section behaviour for the MOBILE panel only — the desktop
  // right-edge rail is identical across sections. The first two
  // sections (home / register) get the soft white-to-transparent
  // gradient so the rail blends into the cream hero + register
  // scenes; the next two (story / features) drop the gradient so the
  // rail floats on the section's own art; sections 5+ hide the rail
  // entirely so the staff roster and campaign book are unobstructed.
  const sectionIds = useMemo(() => [...SECTION_IDS], []);
  const active = useActiveSection(sectionIds);
  const mobileVisible =
    active === null ||
    GRADIENT_SECTIONS.has(active) ||
    TRANSPARENT_SECTIONS.has(active);
  const mobileBgClass =
    active && TRANSPARENT_SECTIONS.has(active)
      ? 'bg-[linear-gradient(to_top,_#634645_0%,_#634645_25%,_transparent_100%)]'
      : 'bg-[linear-gradient(to_top,_white_0%,_white_85%,_transparent_100%)]';

  return (
    <>
    <aside
      aria-label="Quick actions"
      className="pointer-events-none fixed right-0 top-1/2 z-30 hidden -translate-y-1/2 pr-3 md:block lg:pr-[50px]"
    >
      <ul className="pointer-events-auto flex flex-col items-end gap-[22px] xl:mt-12">
        {ITEMS.map((item) => {
          const inner = (
            <>
              <img
                src={item.src}
                alt=""
                className="block h-auto w-full select-none drop-shadow-[0_8px_18px_rgba(0,0,0,0.18)]"
                draggable={false}
              />
              <span
                aria-hidden="true"
                className="cta-label pointer-events-none absolute inset-x-0 bottom-[4%] font-thai text-lg font-bold leading-none xl:text-xl"
              >
                {item.label}
              </span>
            </>
          );
          const sharedClass =
            'group relative block w-[70px] md:w-[100px] lg:w-[120px] xl:w-[140px] 2xl:w-[120px] transition-transform duration-200 hover:-translate-y-1 hover:scale-[1.03]';
          return (
            <li key={item.key}>
              {/* Desktop rail items all stay scroll anchors — the
                  register slot scrolls to <RegisterSection>, whose own
                  CTA opens the modal / grays out when closed. */}
              <a href={item.href} aria-label={item.label} className={sharedClass}>
                {inner}
              </a>
            </li>
          );
        })}
      </ul>
    </aside>

    {/* Mobile design — Figma 136:3094. The desktop rail's fixed
        right-edge column doesn't fit on a phone, so on small screens
        we lay the same four chibi CTAs out in a horizontal row inside
        a fixed bottom panel with a white background, and stack the
        wooden "ลงทะเบียน" register button + Google Play / App Store
        badges (all three reused as-is from HeroSection) below it. */}
    <aside
      aria-label="Quick actions"
      aria-hidden={!mobileVisible}
      // Background swaps per active section (see component head):
      //   • home / register → white-to-transparent gradient
      //   • story / features → fully transparent
      //   • staff / campaign → panel fades out + becomes
      //     non-interactive so the section content is unobstructed
      // Opacity transition gives a soft fade as the user scrolls
      // across the threshold rather than a hard pop.
      className={`fixed inset-x-0 bottom-0 z-30 flex flex-col items-center gap-2 px-3 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] transition-opacity duration-300 ease-out md:hidden ${mobileBgClass} ${
        mobileVisible
          ? 'pointer-events-auto opacity-100'
          : 'pointer-events-none opacity-0'
      }`}
    >
      <ul className="flex w-full items-end justify-center gap-2">
        {ITEMS.map((item) => {
          const inner = (
            <>
              <img
                src={item.src}
                alt=""
                className="block h-auto w-full select-none drop-shadow-[0_4px_8px_rgba(0,0,0,0.18)]"
                draggable={false}
              />
              {/* Label baked-look — same `cta-label` treatment the
                  desktop rail uses, sized down to read inside the
                  smaller chibi tile. */}
              <span
                aria-hidden="true"
                className="cta-label pointer-events-none absolute inset-x-0 bottom-[10%] font-thai text-[13px] font-bold leading-none"
              >
                {item.label}
              </span>
            </>
          );
          const sharedClass =
            'group relative block w-full text-left transition-transform duration-200 hover:-translate-y-1 hover:scale-[1.03]';
          return (
            <li key={item.key} className="min-w-0 flex-1 max-w-[80px]">
              {/* The register chibi scrolls to <RegisterSection> (like
                  the desktop rail) instead of opening the modal — only
                  the wooden cta-join button below opens it. */}
              <a href={item.href} aria-label={item.label} className={sharedClass}>
                {inner}
              </a>
            </li>
          );
        })}
      </ul>

      {/* Register CTA — same cta-join.png used by HeroSection's
          desktop register button. Opens the email-registration
          modal via the shared RegisterModalContext so this CTA and
          the big wooden button inside RegisterSection drive the
          same dialog instance. */}
      {/* <button
        type="button"
        onClick={openRegisterModal}
        disabled={registrationClosed || REGISTRATION_DISABLED}
        aria-label={
          registrationClosed || REGISTRATION_DISABLED
            ? t('register.closed')
            : 'ลงทะเบียน'
        }
        title={
          registrationClosed || REGISTRATION_DISABLED
            ? t('register.closed')
            : undefined
        }
        className="block transition hover:-translate-y-0.5 active:translate-y-0 disabled:grayscale disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
      >
        <img
          src={`/images/cta-join-${lang}.png`}
          alt=""
          aria-hidden="true"
          className="h-[44px] w-auto select-none"
          draggable={false}
        />
      </button> */}

      {/* Store badges — reused from HeroSection. */}
      <div className="flex items-center justify-center gap-3 w-[50%]">
        <a
          href={appStoreUrl(lang)}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Download on the App Store"
          onClick={() => trackStoreClick('apple')}
          className="block transition hover:-translate-y-0.5"
        >
          <img
            src="/images/store-app-store.png"
            alt="Download on the App Store"
            className="h-8 w-auto select-none object-contain"
            draggable={false}
          />
        </a>
        <a
          href={googlePlayUrl(lang)}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Get it on Google Play"
          onClick={() => trackStoreClick('google')}
          className="block transition hover:-translate-y-0.5"
        >
          <img
            src="/images/store-google-play.png"
            alt="Get it on Google Play"
            className="h-8 w-auto select-none object-contain"
            draggable={false}
          />
        </a>
        
      </div>
    </aside>
    </>
  );
}
